import './styles.css';
import { loadCatalog } from './catalog.js';
import { clearFilters, countActiveFilters, filterVenues, renderChips } from './filters.js';
import {
  byEpisodeDateDesc,
  el,
  fold,
  formatEpisodeDate,
  MIN_ZONE,
  programHost,
  programLongName,
  zoneSlug,
} from './format.js';
import { distanceKm, locate } from './geo.js';
import { icon } from './icons.js';
import { emptyState, renderList, resetLists } from './list-view.js';
import { createDeferredMap } from './map-deferred.js';
import { isMapConfigured } from './map-key.js';
import { programSummary, renderDetail } from './detail-view.js';
import { createReportView } from './report-view.js';
import {
  HOME_TITLE,
  episodeHead,
  episodesHead,
  episodesSub,
  guideSummary,
  programHead,
  venueHead,
  zoneHead,
} from '../worker/pagine.js';
import {
  GUIDE_CREDIT,
  GUIDE_ID,
  byStars,
  guideByCity,
  guideCityHead,
  guideCitySlug,
  guideCityTitle,
  isGuide,
  onTelevision,
} from './guida.js';
import { programBar } from './programs.js';
import { episodeStoryHtml } from './racconti.js';
import {
  STATUS_LINK,
  STATUS_PATH,
  STATUS_PROGRAM,
  STATUS_TITLE,
  statusGroups,
  statusIntro,
  statusSub,
} from './chiusi.js';
import {
  episodeCounts,
  episodeIndex,
  episodeName,
  episodeRows,
  episodeSubtitle,
  episodeTitleAndTown,
  episodesByMonth,
  episodesPath,
  FOLLOW,
  onAirEpisodes,
  siblingEpisodes,
  venueEpisodeKey,
  winnersFirst,
} from './puntate.js';
import {
  createAppState,
  forgetSearches,
  rememberSearch,
  toggleFavourite,
} from './state.js';

const $ = (id) => document.getElementById(id);

/**
 * Publishes the real height of the viewport as `--app-height`.
 *
 * Two separate decisions here, and they pull in opposite directions.
 *
 * The value comes from `window.innerHeight`, not `visualViewport.height`,
 * because the latter collapses when the on-screen keyboard opens over the
 * search field and would shrink the whole shell to the strip above it.
 *
 * But the *trigger* has to include the visual viewport's own events. Mobile
 * browsers commonly do not fire `resize` on the window when their chrome
 * slides away — only `visualViewport` hears about it. Listening to `resize`
 * alone left the shell stuck at the height it had while the toolbar was
 * showing: once the toolbar retracted, the viewport grew, the shell did not,
 * and the leftover strip at the bottom was the page showing underneath. That
 * is also why it came and went — it depended entirely on where the browser
 * had put its toolbar at that moment.
 */
function trackViewportHeight() {
  const apply = () => {
    document.documentElement.style.setProperty(
      '--app-height',
      `${window.innerHeight}px`
    );
    window.dispatchEvent(new CustomEvent('vem:viewport'));
  };
  apply();
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
  window.visualViewport?.addEventListener('resize', apply);
  window.visualViewport?.addEventListener('scroll', apply);
}

trackViewportHeight();

/** Italian agreement: one is "1 locale", more than one is "2 locali". */
function count(n, singular, plural) {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * The shape of a card, before there is a card.
 *
 * Drawn from the same measurements as the real thing, so nothing moves when
 * the catalogue arrives. It is deliberately not a spinner: a spinner says
 * "wait", where this says "a list is coming, and it looks like this".
 */
function skeletonCard() {
  const card = el('div', 'card card--ghost');
  card.setAttribute('aria-hidden', 'true');

  const row = el('div', 'card__row');

  const text = el('div', 'ghost__stack');
  text.append(el('div', 'ghost ghost--title'));
  text.append(el('div', 'ghost ghost--line'));
  row.append(text);
  card.append(row);

  const meta = el('div', 'card__meta');
  meta.append(el('div', 'ghost ghost--pill'));
  meta.append(el('div', 'ghost ghost--pill ghost--pill-short'));
  card.append(meta);

  return card;
}

/**
 * What is on screen while the catalogue is being fetched.
 *
 * Two things, and the second is the reason the first is worth anything. The
 * lists get skeletons; and on a wide window the list column is put up front
 * straight away, instead of waiting for `applyLayout` to do it after the
 * fetch. Without that the desktop spends the whole load looking at the map
 * screen, where a skeleton list would be behind it and seen by nobody.
 *
 * On a phone the map is still the opening tab and these are not seen unless
 * somebody changes tab while waiting — which is exactly when they help.
 */
function showLoadingShell() {
  for (const id of ['list-lista', 'list-vicino', 'list-preferiti']) {
    const fragment = document.createDocumentFragment();
    for (let i = 0; i < 4; i += 1) fragment.append(skeletonCard());
    $(id).replaceChildren(fragment);
  }

  if (!window.matchMedia('(min-width: 1000px)').matches) return;
  document.body.classList.add('is-wide');
  for (const screen of document.querySelectorAll('.screen')) {
    screen.classList.toggle('is-active', screen.id === 's-lista');
  }
  for (const tab of document.querySelectorAll('.tab')) {
    tab.classList.toggle('is-active', tab.dataset.tab === 's-lista');
  }
}

function showFatal() {
  for (const id of ['list-lista', 'list-vicino', 'list-preferiti']) {
    $(id).replaceChildren(
      emptyState(
        'Catalogo non caricato',
        'Non è stato possibile leggere i dati. Riprova più tardi.'
      )
    );
  }
}

async function start() {
  // What the Worker had already written into the page, read before the loading
  // shell moves the screens: a venue's card, or a screen of its own. A page it
  // did not know (a 404) arrives with neither. See `keepServed` below.
  const served = document.querySelector('#detail.is-open')
    ? 'detail'
    : document.querySelector('.screen.is-active:not(#s-mappa)')?.id ?? null;
  showLoadingShell();

  let catalog;
  try {
    catalog = await loadCatalog();
  } catch (error) {
    console.error(error);
    showFatal();
    return;
  }

  const { venues, programs, issues } = catalog;

  // Data problems are the maintainer's business, not the visitor's: they go to
  // the console so the page still renders everything that is usable.
  if (issues.length) {
    console.warn(`Catalogo: ${issues.length} problemi rilevati`);
    for (const issue of issues) console.warn(`  - ${issue}`);
  }

  const programNames = new Map(programs.map((program) => [program.id, program.nome]));
  const venuesById = new Map(venues.map((venue) => [venue.id, venue]));

  const state = createAppState();
  /** What the nearby screen says about where its ordering comes from. */
  state.meLabel = 'Posizione non ancora attiva';
  /**
   * How the list screen is sorted. Not remembered between visits on purpose:
   * it is a way of reading the catalogue in the moment, not a preference, and
   * coming back to a list mysteriously in a different order than the one it
   * opens in would be worse than retyping a choice that takes one tap.
   */
  state.ordine = 'recenti';

  let openVenueId = null;
  /** `showTab` runs before the first render; nothing to redraw until it has. */
  let catalogReady = false;
  /** The programme on screen, when one is: its address. */
  let openProgramId = null;
  /**
   * The zone on screen, when one is. It borrows the programme's screen: both
   * are a heading over a list of venues, and the two are never open at once.
   */
  let openZoneSlug = null;
  /** The episode on screen, when one is: it borrows the same screen. */
  let openEpisodeSlug = null;

  /** Zones with a page of their own, by address: the same rule as the Worker's. */
  const zonesBySlug = new Map();
  {
    const counts = new Map();
    for (const venue of onTelevision(venues)) {
      if (venue.zona) counts.set(venue.zona, (counts.get(venue.zona) ?? 0) + 1);
    }
    for (const [zona, n] of counts) {
      if (n >= MIN_ZONE) zonesBySlug.set(zoneSlug(zona), { zona, n });
    }
  }

  /** Episodes with a page of their own: the same rule as the Worker's. */
  const episodes = episodeIndex(episodeRows(venues));

  /** The guide's towns, by address (`/stellati/milano`): the same rule as the Worker's. */
  const guideCities = guideByCity(venues);
  /** The guide's town on screen, when one is: it borrows the programme's screen too. */
  let openGuideCitySlug = null;
  /** Cucine da Incubo's closed and open (`STATUS_PATH`), on the programme's screen too. */
  let openStatus = false;

  const mapView = createDeferredMap($('map'), {
    programNames,
    onSelect: selectPin,
    onDetails: openDetail,
  });

  const reportView = createReportView({
    container: $('report-body'),
    onClose: () => leaveOverlay(closeReportDom),
  });

  /** Everything a card needs to draw itself and report back. */
  const ctx = {
    programNames,
    // Serve alla riga del piazzamento: per dire "non ha vinto" bisogna poter
    // vedere chi ha vinto quella puntata.
    venues,
    state,
    onOpen: openDetail,
    onToggleFav: toggleFav,
    onOpenProgram: openProgram,
    onOpenZone: openZone,
    zonesBySlug,
    onOpenEpisode: openEpisode,
    episodes,
    onOpenGuideCity: openGuideCity,
    onOpenReport: openReport,
    onShowOnMap: showOnMap,
  };

  function visibleVenues() {
    return filterVenues(venues, state, programNames).sort(byEpisodeDateDesc);
  }

  function byDistance(a, b) {
    if (!state.me) return byEpisodeDateDesc(a, b);
    const far = (venue) =>
      venue.lat === null || venue.lng === null
        ? Number.POSITIVE_INFINITY
        : distanceKm(state.me, { lat: venue.lat, lng: venue.lng });
    return far(a) - far(b);
  }

  /** Italian collation, so "È" files with "E" and not after "Z". */
  function byName(a, b) {
    return (a.nome ?? a.id).localeCompare(b.nome ?? b.id, 'it');
  }

  /**
   * The list screen's own order.
   *
   * Only that screen's. "Vicino" is sorted by distance because that is what it
   * is for, favourites by episode, the map not at all — giving one control
   * power over all of them would mean a choice made in one place silently
   * rearranging three others.
   */
  function inListOrder(list) {
    if (state.ordine === 'nome') return [...list].sort(byName);
    if (state.ordine === 'vicini') return [...list].sort(byDistance);
    return list;
  }

  function renderProgramRows() {
    const fragment = document.createDocumentFragment();
    // The guide after the programmes, under a heading of its own: it is not
    // one of them (`homeProgramRows` in `worker/pagine.js` writes the same).
    const ordered = [
      ...programs.filter((program) => program.id !== GUIDE_ID),
      ...programs.filter((program) => program.id === GUIDE_ID),
    ];
    for (const program of ordered) {
      const guide = program.id === GUIDE_ID;
      const owned = venues.filter((venue) => venue.programma === program.id).length;
      if (guide && owned) fragment.append(el('p', 'group-label', 'Guide'));
      /*
       * A link with the programme's address, not a button: the Worker writes
       * the same rows into the home page, and a search engine follows links,
       * not click handlers. A click still opens the screen in place.
       */
      const row = el(owned ? 'a' : 'span', owned ? 'row-link' : 'row-link is-disabled');
      if (owned) row.href = `/programma/${program.id}`;

      const text = el('span');
      text.append(el('b', null, guide ? `★ ${program.nome}` : program.nome));
      text.append(
        el(
          'small',
          null,
          owned
            ? guide
              ? `${count(owned, 'ristorante', 'ristoranti')}, città per città`
              : `${count(owned, 'locale', 'locali')} in catalogo`
            : 'nessun locale ancora'
        )
      );
      row.append(programBar(program.id), text, icon('chevron'));
      if (owned) {
        row.addEventListener('click', (event) => {
          event.preventDefault();
          openProgram(program.id);
        });
      }
      fragment.append(row);
    }
    $('list-programmi').replaceChildren(fragment);
  }

  /**
   * The past searches, offered only when there is room for them to be useful:
   * the overlay is up and nothing has been typed yet. As soon as a letter is
   * typed the results below are the better answer and the list gets out of
   * the way.
   */
  function renderRecenti() {
    const box = $('recenti');
    const offering =
      $('search').classList.contains('is-open') &&
      state.q.trim() === '' &&
      state.recenti.length > 0;

    box.hidden = !offering;
    if (!offering) {
      $('chips-recenti').replaceChildren();
      return;
    }

    const row = document.createDocumentFragment();
    for (const entry of state.recenti) {
      const chip = el('button', 'chip');
      chip.type = 'button';
      chip.setAttribute('aria-pressed', 'false');
      chip.append(el('span', 'chip__label', entry));
      chip.addEventListener('click', () => {
        state.q = entry;
        $('q').value = entry;
        render();
      });
      row.append(chip);
    }

    const forget = el('button', 'chip chip--ghost');
    forget.type = 'button';
    forget.setAttribute('aria-pressed', 'false');
    forget.append(el('span', 'chip__label', 'Dimentica'));
    forget.addEventListener('click', () => {
      forgetSearches(state);
      render();
    });
    row.append(forget);

    $('chips-recenti').replaceChildren(row);
  }

  /**
   * What the colours on the map mean.
   *
   * Four statuses and a ring, and until now nothing anywhere said so: the map
   * was the only screen where the colour carried information and the only one
   * with no way to look it up. It lives beside the filters because that is
   * where somebody goes when they want to change what the map is showing,
   * which is the same moment they wonder what it is showing now.
   *
   * Built once. It is the only thing on this screen that cannot change.
   */
  function renderLegend() {
    const rows = [
      ['aperto', 'Aperto'],
      ['chiuso', 'Chiuso'],
      ['chiuso_temporaneamente', 'Chiuso temporaneamente'],
      ['ignoto', 'Stato non verificato'],
    ];

    const box = document.createDocumentFragment();
    for (const [stato, label] of rows) {
      const item = el('span', 'legend__item');
      item.append(el('i', `legend__dot legend__dot--${stato}`));
      item.append(el('span', null, label));
      box.append(item);
    }

    const winner = el('span', 'legend__item');
    winner.append(el('i', 'legend__dot legend__dot--winner'));
    winner.append(el('span', null, 'Vincitore di puntata'));
    box.append(winner);

    $('legend').replaceChildren(box);
  }

  /** Draws the list belonging to the screen on show, and the search if it is up. */
  const LISTE = {
    's-lista': 'list-lista',
    's-vicino': 'list-vicino',
    's-preferiti': 'list-preferiti',
    's-puntate': 'list-puntate',
  };

  function renderVisibleLists(shown, distance) {
    const active = document.querySelector('.screen.is-active')?.id;

    // A list left behind by a screen nobody is on keeps its cards in the page
    // for as long as the visit lasts. Emptying it costs nothing — it is built
    // again before its screen is shown — and it keeps the page the size of
    // what is on screen rather than of everywhere that has been.
    for (const [screen, list] of Object.entries(LISTE)) {
      if (screen !== active) $(list).replaceChildren();
    }
    if (!$('search').classList.contains('is-open')) $('list-search').replaceChildren();

    if (active === 's-lista') {
      renderList($('list-lista'), inListOrder(shown), ctx, { distance });
    } else if (active === 's-vicino') {
      renderList($('list-vicino'), [...shown].sort(byDistance), ctx, {
        distance: true,
      });
    } else if (active === 's-puntate') {
      renderEpisodesTab();
    } else if (active === 's-preferiti') {
      renderList(
        $('list-preferiti'),
        venues.filter((venue) => state.fav.has(venue.id)).sort(byEpisodeDateDesc),
        ctx,
        {
          distance,
          emptyTitle: 'Ancora nessun preferito',
          emptyText: 'Tocca il cuore su un locale per ritrovarlo qui.',
        }
      );
    }

    // The search sits over whichever screen is up, so it is drawn on its own
    // terms rather than as one of them.
    if ($('search').classList.contains('is-open')) {
      renderSearchEpisodes();
      renderList($('list-search'), shown, ctx, { distance });
    }
  }

  /**
   * The episodes a search finds, above the venues: by title or town, from
   * every programme, newest first. Somebody typing "riso al salto" is looking
   * for the episode, and its four venues are named nothing like it. A
   * programme's chip narrows them as it narrows the venues.
   */
  const SEARCH_EPISODES = 5;
  let searchEpisodesAll = false;

  function searchEpisodes() {
    const needle = fold(state.q).trim();
    if (needle.length < 2) return [];
    const shows = new Set([...state.filtri.programmi].filter((id) => id !== GUIDE_ID));
    return [...episodes.bySlug.values()]
      .filter((episode) => !shows.size || shows.has(episode.programma))
      .filter((episode) => fold(`${episode.titolo ?? ''} ${episode.citta ?? ''}`).includes(needle))
      .sort((a, b) => (b.data ?? '').localeCompare(a.data ?? '') || a.key.localeCompare(b.key));
  }

  function renderSearchEpisodes() {
    const found = searchEpisodes();
    const list = $('list-q-puntate');
    $('q-puntate').hidden = found.length === 0;
    if (!found.length) {
      list.replaceChildren();
      return;
    }
    $('q-puntate-label').textContent = `Puntate per «${state.q.trim()}»`;
    const items = [];
    for (const episode of searchEpisodesAll ? found : found.slice(0, SEARCH_EPISODES)) {
      const date = formatEpisodeDate({ data: episode.data, precisione: 'giorno' });
      const programName = programNames.get(episode.programma) ?? episode.programma;
      const item = el('li');
      const link = el('a', null, episodeTitleAndTown(episode));
      link.href = `/puntata/${episode.slug}`;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        openEpisode(episode.slug);
      });
      item.append(
        link,
        document.createTextNode(' '),
        el('small', null, `· ${[programName, date].filter(Boolean).join(' · ')}`)
      );
      items.push(item);
    }
    if (!searchEpisodesAll && found.length > SEARCH_EPISODES) {
      const item = el('li');
      const more = el('button', 'link-btn', `Mostra tutte le ${found.length} puntate`);
      more.type = 'button';
      more.addEventListener('click', () => {
        searchEpisodesAll = true;
        renderSearchEpisodes();
      });
      item.append(more);
      items.push(item);
    }
    list.replaceChildren(...items);
  }

  /**
   * Set when a filter moved, so the map re-frames itself exactly once.
   *
   * Not on every redraw: those happen for reasons that have nothing to do
   * with what the map is looking at — a favourite, a tab, a status coming
   * back from the panel — and re-framing on any of them would take the map
   * away from somebody who had just panned it somewhere on purpose.
   *
   * Not on the typed query either, even though that also narrows the set: on
   * a wide window the map is beside the field, and a map that lurches on
   * every few keystrokes is a map nobody can read while typing.
   */
  let refitWanted = false;

  /**
   * What the open programme, zone, episode or town lists, and the map's whole
   * content while that screen is beside it.
   *
   * The map used to frame the episode and keep every other pin on: the
   * maintainer opened the latest Foodish episode and saw the whole of Rome,
   * with nothing to tell its four restaurants from the rest. The screen is a
   * filter of its own, and it lasts as long as the screen is open; the chips
   * are left as they were, so closing it brings back the map from before.
   */
  let screenVenues = null;

  /**
   * The same on a phone, where the list covers the map: set by that screen's
   * "Vedi sulla mappa", and kept on the map until the pill at its foot, a
   * tab, or a change of filters lets it go.
   */
  let mapFocus = null;

  function focusOnMap(listed, label) {
    goToTab('s-mappa');
    mapFocus = { venues: listed, label };
    render();
  }

  /** The focus the map was last framed on: a new one is framed once. */
  let framedFocus = null;

  /**
   * Whether the search was opened from the map, which is what earns it the
   * bar at its foot.
   *
   * From the map the screen is a filter for the map, and the maintainer found
   * the way back was a "Chiudi" that read like "cancel": choose Foodish, see
   * the list change, and nothing said that closing would show those pins.
   * The bar is the pattern of every site that filters a map on a phone —
   * Airbnb, Booking, Immobiliare: "Azzera" on the left, "Mostra N" on the
   * right, and the button takes you back where you were. Opened from the
   * search tab the visitor came to type, so there is no map to go back to.
   */
  let searchFromMap = false;

  /** The bar at the foot of the search, when it is the map's filter. */
  function renderSearchFoot() {
    const foot = $('search-foot');
    foot.hidden =
      !searchFromMap ||
      !$('search').classList.contains('is-open') ||
      document.body.classList.contains('is-wide');
    if (foot.hidden) return;

    // Without the query: closing the search drops it, so the map will show
    // what the filters leave and the button has to count exactly that.
    const n = filterVenues(venues, { filtri: state.filtri, q: '' }, programNames).length;
    const show = $('search-show');
    show.disabled = n === 0;
    show.textContent = n === 0 ? 'Nessun locale' : `Mostra ${count(n, 'locale', 'locali')}`;
    $('search-reset').disabled = countActiveFilters(state.filtri) === 0;
  }

  function onFiltersChanged() {
    refitWanted = true;
    mapFocus = null;
    resetLists();
    render();
  }

  function render() {
    const chipContext = { venues, programs, state, onChange: onFiltersChanged };
    renderChips($('chips-lista'), chipContext);
    const footShown = searchFromMap && !document.body.classList.contains('is-wide');
    renderChips($('chips-search'), chipContext, { reset: !footShown });
    renderSearchFoot();

    const shown = visibleVenues();

    $('btn-filters').classList.toggle('is-on', countActiveFilters(state.filtri) > 0);

    // Once the position is the device's own, the distance belongs on every card
    // and not only on the screen named after it: "how far is that" is asked
    // wherever a venue is read. Without one there is nothing to measure from.
    const distance = state.meExact;

    // Only the list somebody is looking at is built.
    //
    // All four used to be rebuilt on every change — every filter, every
    // favourite, every redraw — and three of them were behind a screen nobody
    // was on. On forty venues that is invisible; measured on twenty thousand,
    // the four lists were 98% of a million DOM nodes, far more than the map
    // that was blamed for it. A list is drawn when its screen comes up, which
    // is `showTab`'s job, and rebuilt here only while it is the one on show.
    //
    // Cheap, and it does not change what anybody sees: a hidden list that is
    // one redraw out of date is a list that gets redrawn before it is seen.
    // Just the count: "most recent first" used to follow it, and was wrong
    // the moment somebody sorted A–Z or by distance.
    $('lista-sub').textContent = count(shown.length, 'locale', 'locali');
    $('vicino-sub').textContent = state.meLabel;
    $('q-label').textContent = state.q
      ? `Locali per «${state.q}»`
      : 'Tutti i locali';
    // The map's own field, beside the list, says what the search says.
    if ($('q-map').value !== state.q) $('q-map').value = state.q;

    renderVisibleLists(shown, distance);
    renderRecenti();

    for (const button of document.querySelectorAll('.seg__btn')) {
      const on = button.dataset.ordine === state.ordine;
      button.classList.toggle('is-on', on);
      button.setAttribute('aria-pressed', String(on));
    }

    /*
     * How many the filters left, and the way through to reading them.
     *
     * It counts what the list would show rather than the pins, because that
     * is where it leads: a venue with no coordinates has no pin but is in the
     * list all the same, and a number that did not match its destination
     * would be worse than no number. Hidden on a wide window, where the list
     * is already open beside the map and would be counting itself.
     */
    const wide = document.body.classList.contains('is-wide');
    const focus = wide
      ? $('s-programma').classList.contains('is-active') ? screenVenues : null
      : mapFocus?.venues ?? null;

    const pill = $('map-count');
    pill.hidden = wide || (!mapFocus && shown.length === 0);
    pill.textContent = mapFocus
      ? `${mapFocus.label} · mostra tutti ✕`
      : `${count(shown.length, 'locale', 'locali')} · vedi elenco`;

    renderProgramRows();
    $('stat-fav').textContent = String(state.fav.size);
    $('stat-tot').textContent = String(onTelevision(venues).length);

    // A card opened over the screen, from the search or a link, for a venue
    // the screen does not list: its pin is drawn too, or the map would fly to
    // nothing.
    const opened = openVenueId ? venuesById.get(openVenueId) : null;
    const drawn = focus && opened && !focus.includes(opened) ? [...focus, opened] : focus;
    mapView.update(drawn ?? shown, state.attivo);
    if (focus && focus !== framedFocus) mapView.fitTo(focus);
    framedFocus = focus;
    if (refitWanted) {
      refitWanted = false;
      mapView.fitTo(focus ?? shown);
    }

    const total = state.filtri.programmi.has(GUIDE_ID) ? venues.length : onTelevision(venues).length;
    $('status').textContent = `${count(shown.length, 'locale', 'locali')} su ${total}`;
  }

  /* ---------- navigation ---------- */

  function showTab(id, markTab = true) {
    for (const screen of document.querySelectorAll('.screen')) {
      screen.classList.toggle('is-active', screen.id === id);
    }
    for (const tab of document.querySelectorAll('.tab')) {
      tab.classList.toggle('is-active', tab.dataset.tab === id);
    }
    if (markTab) state.tab = id;

    // The screen just revealed may be holding a list from several changes ago,
    // or none at all. It is built now, while it is the active one.
    if (catalogReady) render();

    // The map was laid out while hidden, so it needs its size back — and it
    // may not have been fetched yet, which is the other thing this asks for.
    if (id === 's-mappa') {
      mapView.load();
      setTimeout(() => mapView.resize(), 60);
    }

    // Somebody who opens "Vicino" has already said what they want. This is the
    // moment a browser is meant to ask for a position: on an intention, not on
    // arrival. Showing them an inert list with a button to press first would
    // only be asking them to say it twice.
    if (id === 's-vicino' && !located) onLocate();
  }

  function syncDetailFav(venueId) {
    const saved = state.fav.has(venueId);
    $('d-fav').classList.toggle('is-on', saved);
    $('d-fav').setAttribute('aria-pressed', String(saved));
  }

  /* ---------- overlays and the way back ---------- */

  /**
   * Every overlay answers the phone's back gesture.
   *
   * On a phone that gesture is the first thing anyone reaches for, and without
   * a history entry behind it, back leaves the site instead of closing what is
   * open. Opening an overlay pushes one entry; the on-screen controls go
   * through history too, so the entry and the screen can never disagree. An
   * overlay that takes another's place inherits its entry rather than stacking
   * a second one.
   */
  let overlayDepth = 0;

  /**
   * The focus follows what is on screen.
   *
   * An overlay used to appear without the focus going anywhere near it: the
   * page behind kept it, so a keyboard carried on tabbing through controls
   * the visitor could no longer see, and a screen reader went on reading a
   * screen that was covered. Opening one moves the focus inside it; closing
   * one hands it back to the control that opened it, which is where somebody
   * navigating without a mouse expects to find themselves.
   *
   * A stack rather than one slot, because these nest: the report opens over
   * the venue card, which may itself have opened over the search.
   */
  const focusStack = [];

  /**
   * Remembered as an element *and* as the venue it belongs to.
   *
   * The element alone is not enough: closing an overlay redraws the list
   * underneath, `replaceChildren` throws the old cards away, and the node we
   * were holding is no longer in the page. Found again by id, the focus lands
   * on the card the visitor actually came from rather than nowhere.
   */
  function takeFocus(node) {
    const from = document.activeElement;
    focusStack.push({
      node: from,
      venueId: from?.closest?.('[data-venue-id]')?.dataset.venueId ?? null,
    });
    node?.focus();
  }

  /** Whether a control is really there to be focused, and not behind a lid. */
  function reachable(node) {
    if (!node || !node.isConnected) return false;
    return !node.closest(
      '.search:not(.is-open), .detail:not(.is-open), .report:not(.is-open)'
    );
  }

  function giveFocusBack() {
    const back = focusStack.pop();
    if (!back) return;

    if (reachable(back.node)) {
      back.node.focus();
      return;
    }

    // The card was rebuilt while the overlay was up. The venue it stood for
    // has not changed, so its new card is the same place to come back to.
    if (back.venueId) {
      const active = document.querySelector('.screen.is-active');
      const again = active?.querySelector(
        `[data-venue-id="${CSS.escape(back.venueId)}"] .card__open`
      );
      if (reachable(again)) {
        again.focus();
        return;
      }
    }

    // Nothing to go back to. Anywhere is better than a control inside the
    // overlay that has just closed, which is invisible and still focused.
    if (!reachable(document.activeElement)) document.activeElement.blur();
  }

  /**
   * The address of whatever is open.
   *
   * There is no router: the app is one page, and the two things worth a link
   * of their own — a venue and a programme — are read back off the same
   * screens that already know they are showing them. `/` is not a state, it is
   * the absence of the other two.
   */
  function currentPath() {
    if (openVenueId) return `/locale/${openVenueId}`;
    if (openProgramId && $('s-programma').classList.contains('is-active')) {
      return `/programma/${openProgramId}`;
    }
    if (openZoneSlug && $('s-programma').classList.contains('is-active')) {
      return `/zona/${openZoneSlug}`;
    }
    if (openEpisodeSlug && $('s-programma').classList.contains('is-active')) {
      return `/puntata/${openEpisodeSlug}`;
    }
    if (openGuideCitySlug && $('s-programma').classList.contains('is-active')) {
      return `/stellati/${openGuideCitySlug}`;
    }
    if (openStatus && $('s-programma').classList.contains('is-active')) return STATUS_PATH;
    if ($('s-puntate').classList.contains('is-active')) return episodesPath(episodesProgram);
    return '/';
  }

  /**
   * The title of whatever is open, the same words the Worker puts in the page
   * it serves at that address (`worker/pagine.js`, shared rather than copied).
   * Without it the tab, the history, a bookmark and the visit statistics all
   * kept the title of the page the visit started on.
   */
  function currentTitle() {
    const programmeShown = $('s-programma').classList.contains('is-active');
    if (openVenueId) {
      const venue = venuesById.get(openVenueId);
      if (venue) {
        return venueHead(venue, programNames.get(venue.programma) ?? venue.programma).title;
      }
    }
    if (openProgramId && programmeShown) {
      const name = programNames.get(openProgramId) ?? openProgramId;
      return programHead(
        openProgramId,
        name,
        venues.filter((venue) => venue.programma === openProgramId)
      ).title;
    }
    if (openZoneSlug && programmeShown) {
      const zone = zonesBySlug.get(openZoneSlug);
      if (zone) {
        return zoneHead(
          zone.zona,
          onTelevision(venues).filter((venue) => venue.zona === zone.zona),
          programNames
        ).title;
      }
    }
    if (openEpisodeSlug && programmeShown) {
      const episode = episodes.bySlug.get(openEpisodeSlug);
      if (episode) {
        return episodeHead(
          episode,
          venues.filter((venue) => venueEpisodeKey(venue) === episode.key),
          episode.programma,
          programNames.get(episode.programma) ?? episode.programma
        ).title;
      }
    }
    if (openStatus && programmeShown) return STATUS_TITLE;
    if (openGuideCitySlug && programmeShown) {
      const city = guideCities.get(openGuideCitySlug);
      if (city) return guideCityHead(city.citta, city.venues).title;
    }
    if ($('s-puntate').classList.contains('is-active')) {
      const latest = episodesLatest();
      return episodesHead(
        episodesProgram,
        programNames.get(episodesProgram) ?? null,
        episodesShown(),
        latest,
        latest ? episodeWinners.get(latest.key) : null
      ).title;
    }
    return HOME_TITLE;
  }

  /** Brings the bar in line without adding a step to go back through. */
  function syncPath() {
    const path = currentPath();
    if (location.pathname !== path) history.replaceState(history.state, '', path);
    document.title = currentTitle();
  }

  function pushOverlay() {
    overlayDepth += 1;
    // The title first: the entry being pushed is remembered under it.
    document.title = currentTitle();
    history.pushState({ vem: overlayDepth }, '', currentPath());
  }

  function leaveOverlay(close) {
    if (overlayDepth > 0) {
      history.back();
      return;
    }
    close();
    syncPath();
    loadMapIfShown();
  }

  /**
   * The map, fetched the moment it is uncovered. A phone that opened a
   * `/locale/…` link has the map tab under the card without ever having shown
   * it, so closing the card is the first time the map is on screen.
   */
  function loadMapIfShown() {
    const covered = ['detail', 'search', 'report'].some((id) =>
      $(id).classList.contains('is-open')
    );
    if (!covered && $('s-mappa').classList.contains('is-active')) mapView.load();
  }

  /**
   * Opens or closes one of the two panels that slide up over the screen.
   *
   * Closed, they are only moved below the bottom edge, so their buttons were
   * still in the page: Tab walked into "Chiama" or "Invia" on a card nobody
   * could see, and a screen reader announced it. `aria-hidden` hid them from
   * reading and nothing else; `inert` takes them out of the tab order and out
   * of reach as well. It is lifted before anything inside is focused — an
   * inert element cannot take the focus — and set before the focus is given
   * back elsewhere, which is where it has to go anyway.
   */
  function setPanel(id, open) {
    const panel = $(id);
    panel.classList.toggle('is-open', open);
    panel.setAttribute('aria-hidden', String(!open));
    panel.inert = !open;
  }

  function closeDetailDom() {
    openVenueId = null;
    setPanel('detail', false);
    giveFocusBack();
  }

  function closeReportDom() {
    setPanel('report', false);
    giveFocusBack();
  }

  function openReport(venueId) {
    const venue = venuesById.get(venueId);
    if (!venue) return;
    reportView.open(venue);
    setPanel('report', true);
    takeFocus($('close-report'));
    pushOverlay();
  }

  function closeSearchDom() {
    $('search').classList.remove('is-open');
    state.q = '';
    $('q').value = '';
    render();
    giveFocusBack();
  }

  function closeTopOverlay() {
    if ($('report').classList.contains('is-open')) {
      closeReportDom();
    } else if ($('detail').classList.contains('is-open')) {
      closeDetailDom();
    } else if ($('search').classList.contains('is-open')) {
      closeSearchDom();
    } else if ($('s-programma').classList.contains('is-active')) {
      openProgramId = null;
      openZoneSlug = null;
      openEpisodeSlug = null;
      openGuideCitySlug = null;
      openStatus = false;
      showTab(state.tab);
    }
  }

  /** Set when a tab unwinds every overlay at once and closes them itself. */
  let unwinding = false;

  window.addEventListener('popstate', () => {
    if (unwinding) {
      unwinding = false;
      syncPath();
      return;
    }
    overlayDepth = Math.max(0, overlayDepth - 1);
    closeTopOverlay();
    // The entry gone back to may name a screen that was replaced while it was
    // under the overlay — a card opened from an episode's page, then the
    // episode again from the card — so the bar follows what is on screen.
    syncPath();
    loadMapIfShown();
  });

  /**
   * `push` is false only when the address came first — someone opened
   * `/locale/…` directly. Pushing then would put a step behind the page that
   * leads nowhere, and back would leave the site instead of closing the entry.
   */
  function openDetail(venueId, { push = true } = {}) {
    const venue = venuesById.get(venueId);
    if (!venue) return;

    const searchWasOpen = $('search').classList.contains('is-open');
    const detailWasOpen = openVenueId !== null;

    openVenueId = venueId;
    state.attivo = venueId;

    // Where the map stays on screen next to the entry, the two have to be
    // looking at the same place or the pairing means nothing. Where it does
    // not, moving it would be moving something nobody can see.
    if (
      document.body.classList.contains('is-wide') &&
      venue.lat !== null &&
      venue.lng !== null
    ) {
      // A starred restaurant has no pin until the guide's chip is on: turned
      // on here, or the map would fly to an empty spot. Not over a screen
      // that is showing its own venues, which draws the pin by itself.
      const onScreen = $('s-programma').classList.contains('is-active') && screenVenues;
      if (isGuide(venue) && !onScreen) state.filtri.programmi.add(GUIDE_ID);
      mapView.flyTo(venue);
    }

    $('d-bar-name').textContent = venue.nome ?? venue.id;
    syncDetailFav(venueId);
    renderDetail($('d-body'), venue, ctx);
    setPanel('detail', true);

    // Only a card arriving over nothing takes a slot on the stack. One that
    // replaces the search, or another card, inherits the slot already there,
    // exactly as it inherits the history entry a few lines below.
    if (!searchWasOpen && !detailWasOpen) takeFocus($('close-detail'));
    else $('close-detail').focus();

    // The search overlay sits above the detail, so it has to go. Its query goes
    // with it: leaving the catalogue filtered by words nothing on screen still
    // shows would be a trap on the way back. The detail inherits its entry.
    if (searchWasOpen) {
      // Opening a result is the moment a search is known to have worked, so
      // it is the moment worth remembering — and it has to happen before the
      // query is thrown away on the next line.
      rememberSearch(state, state.q);
      $('search').classList.remove('is-open');
      state.q = '';
      $('q').value = '';
      syncPath();
    } else if (!detailWasOpen && push) {
      pushOverlay();
    } else {
      syncPath();
    }
    render();
  }

  /**
   * The guide's towns as chips, each to its page: under the guide's own
   * screen and under each town's (`guideCityLinks` in `worker/pagine.js`).
   */
  function guideCityChips(skip = null, limit = Infinity) {
    const row = el('div', 'chips chips--wrap');
    const towns = [...guideCities.values()]
      .filter((city) => city.slug !== skip)
      .sort((a, b) => b.venues.length - a.venues.length || a.citta.localeCompare(b.citta, 'it'))
      .slice(0, limit);
    for (const city of towns) {
      const link = el('a', 'chip');
      link.href = `/stellati/${city.slug}`;
      link.append(el('span', 'chip__label', city.citta), el('span', 'chip__n', String(city.venues.length)));
      link.addEventListener('click', (event) => {
        event.preventDefault();
        openGuideCity(city.slug, { push: false });
      });
      row.append(link);
    }
    return row;
  }

  function guideCredit() {
    return el('p', 'note', GUIDE_CREDIT);
  }

  function openProgram(programId, { push = true } = {}) {
    openProgramId = programId;
    openZoneSlug = null;
    openEpisodeSlug = null;
    openGuideCitySlug = null;
    openStatus = false;
    const guide = programId === GUIDE_ID;
    const owned = venues
      .filter((venue) => venue.programma === programId)
      .sort(guide ? byStars : byEpisodeDateDesc);

    screenVenues = owned;
    const name = programNames.get(programId) ?? programId;
    $('prog-name').textContent = name;
    $('prog-story').hidden = true;
    $('prog-title').textContent = programLongName(programId, name);
    $('prog-sub').textContent = guide ? guideSummary(owned) : programSummary(owned);
    renderList($('list-programma'), owned, ctx);
    // The way to the programme's episodes, each with its page.
    const episodeTotal = episodeCounts(episodes).get(programId);
    if (episodeTotal) {
      const link = el('a', 'pill-link');
      link.href = episodesPath(programId);
      link.append(icon('tv'), document.createTextNode(`Le puntate di ${name} (${episodeTotal})`));
      link.addEventListener('click', (event) => {
        event.preventDefault();
        openEpisodes(programId);
      });
      $('list-programma').prepend(link);
    }
    // Cucine da Incubo: who closed and who is still open, right after.
    if (programId === STATUS_PROGRAM) {
      const link = el('a', 'pill-link', STATUS_LINK);
      link.href = STATUS_PATH;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        openStatusPage();
      });
      const episodesPill = $('list-programma').querySelector(':scope > .pill-link');
      if (episodesPill) episodesPill.after(link);
      else $('list-programma').prepend(link);
    }
    // The way into the towns, and where the list comes from.
    if (guide) {
      const list = $('list-programma');
      list.prepend(guideCityChips(null, 24));
      list.prepend(el('p', 'block__label', 'Per città'));
      list.append(guideCredit());
    }

    // Reached from a venue's card, the programme screen takes over its entry.
    const detailWasOpen = openVenueId !== null;
    closeDetailDom();
    showTab('s-programma', false);
    if (!detailWasOpen && push) pushOverlay();
    else syncPath();
  }

  /**
   * Every venue in one zone, on the programme's screen. Reached from the
   * "all the venues of …" link under a venue's card, and from `/zona/…`.
   */
  function openZone(slug, { push = true } = {}) {
    const zone = zonesBySlug.get(slug);
    if (!zone) return;
    openZoneSlug = slug;
    openProgramId = null;
    openEpisodeSlug = null;
    openGuideCitySlug = null;
    openStatus = false;
    const owned = onTelevision(venues)
      .filter((venue) => venue.zona === zone.zona)
      .sort(byEpisodeDateDesc);

    screenVenues = owned;
    $('prog-name').textContent = zone.zona;
    $('prog-story').hidden = true;
    $('prog-title').textContent = `${zone.zona}: i ristoranti visti in TV`;
    $('prog-sub').textContent = programSummary(owned);
    renderList($('list-programma'), owned, ctx);

    // The same town in the guide, when it has starred restaurants: the two
    // pages send visitors to each other (`serveZonePage` in the Worker).
    const starred = guideCities.get(guideCitySlug(zone.zona));
    if (starred) {
      const more = el('p', 'links__all');
      const link = el(
        'a',
        null,
        `★ ${zone.zona}: anche ${
          starred.venues.length === 1 ? 'un ristorante stellato' : `${starred.venues.length} stellati`
        } Michelin 2026 →`
      );
      link.href = `/stellati/${starred.slug}`;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        openGuideCity(starred.slug, { push: false });
      });
      more.append(link);
      $('list-programma').append(more);
    }

    const detailWasOpen = openVenueId !== null;
    closeDetailDom();
    showTab('s-programma', false);
    if (!detailWasOpen && push) pushOverlay();
    else syncPath();
  }

  /**
   * Cucine da Incubo, who closed and who is still open, group by group, on
   * the programme's screen. The Worker writes the same (`statusBody` in
   * `worker/pagine.js`); the groups come from `src/chiusi.js`.
   */
  function openStatusPage({ push = true } = {}) {
    openStatus = true;
    openProgramId = null;
    openZoneSlug = null;
    openEpisodeSlug = null;
    openGuideCitySlug = null;
    const owned = venues.filter((venue) => venue.programma === STATUS_PROGRAM);
    const groups = statusGroups(owned);

    screenVenues = owned;
    $('prog-name').textContent = programNames.get(STATUS_PROGRAM) ?? STATUS_PROGRAM;
    $('prog-story').hidden = true;
    $('prog-title').textContent = STATUS_TITLE;
    $('prog-sub').textContent = statusSub(groups);
    const list = $('list-programma');
    list.replaceChildren(el('p', 'status-intro', statusIntro(owned.length)));
    for (const group of groups) {
      list.append(el('h3', 'group-label', `${group.label} (${group.venues.length})`));
      if (group.note) list.append(el('p', 'status-intro', group.note));
      const cards = el('div', 'status-group');
      renderList(cards, group.venues, ctx);
      list.append(cards);
    }

    // From the programme's own screen it takes that screen's place, as an
    // episode does: one entry, so "back" leaves the screen altogether.
    const detailWasOpen = openVenueId !== null;
    const screenWasOpen = $('s-programma').classList.contains('is-active');
    closeDetailDom();
    showTab('s-programma', false);
    if (!detailWasOpen && !screenWasOpen && push) pushOverlay();
    else syncPath();
  }

  /** "Seguici", with the links to Instagram and Facebook. */
  function followBox() {
    const box = el('div', 'follow');
    box.append(el('p', 'follow__text', FOLLOW.text));
    const links = el('p', 'follow__links');
    for (const [name, url] of FOLLOW.links) {
      const link = el('a', 'follow__link', name);
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      links.append(link);
    }
    box.append(links);
    return box;
  }

  /**
   * One episode on the programme's screen: its venues, winner first, then the
   * way on to the other episodes of the programme in the same town. Reached
   * from a venue's card, from the episode line on any card, and from
   * `/puntata/…`. The Worker writes the same into the page
   * (`episodeBody` in `worker/pagine.js`).
   */
  /**
   * "La puntata" and the dish or the place. The page the Worker served already
   * has its own, marked with its address, and keeps it; any other episode loads
   * the texts first, which is why they are a file of their own. If another
   * screen has been opened by the time they arrive, they are not shown.
   */
  function showStory(episode, owned, programName, date) {
    const box = $('prog-story');
    if (box.dataset.slug === episode.slug && box.childElementCount) {
      box.hidden = false;
      return;
    }
    box.hidden = true;
    box.replaceChildren();
    box.dataset.slug = episode.slug;
    import('./racconti-testi.js')
      .then(({ RACCONTI }) => {
        if (openEpisodeSlug !== episode.slug || box.dataset.slug !== episode.slug) return;
        box.innerHTML = episodeStoryHtml(
          episode,
          owned,
          { programName, host: programHost(episode.programma), date },
          RACCONTI
        );
        box.hidden = !box.childElementCount;
      })
      .catch(() => {
        // Without the texts the episode is still its list of venues.
      });
  }

  function openEpisode(slug, { push = true } = {}) {
    const episode = episodes.bySlug.get(slug);
    if (!episode) return;

    // Opened from the search's list of episodes: the search goes, as it does
    // for a venue, and the episode inherits its history entry.
    const searchWasOpen = $('search').classList.contains('is-open');
    if (searchWasOpen) {
      rememberSearch(state, state.q);
      $('search').classList.remove('is-open');
      state.q = '';
      $('q').value = '';
      render();
    }
    openEpisodeSlug = slug;
    openProgramId = null;
    openZoneSlug = null;
    openGuideCitySlug = null;
    openStatus = false;
    const owned = venues
      .filter((venue) => venueEpisodeKey(venue) === episode.key)
      .sort(winnersFirst);

    screenVenues = owned;
    const programName = programNames.get(episode.programma) ?? episode.programma;
    const date = formatEpisodeDate({ data: episode.data, precisione: 'giorno' });
    $('prog-name').textContent = programName;
    $('prog-title').textContent = episodeName(episode, date);
    showStory(episode, owned, programName, date);
    $('prog-sub').textContent = episodeSubtitle(episode, programName, date);
    const list = $('list-programma');
    renderList(list, owned, ctx, { address: true, episodeLink: false });

    // On a phone the list covers the map; the way to the four pins, as on a
    // venue's card. Beside the map the map moves on its own, below.
    if (owned.some((venue) => venue.lat !== null && venue.lng !== null)) {
      const show = el('button', 'hero__map');
      show.type = 'button';
      show.append(icon('pin'), document.createTextNode('Vedi sulla mappa'));
      show.addEventListener('click', () => focusOnMap(owned, episodeName(episode, date)));
      list.prepend(show);
    }

    list.append(followBox());

    const siblings = siblingEpisodes(episodes, episode);
    if (siblings.length) {
      list.append(
        el('p', 'block__label', `Altre puntate di ${programName} · ${episode.citta}`)
      );
      const links = el('ul', 'links');
      for (const other of siblings) {
        const otherDate = formatEpisodeDate({ data: other.data, precisione: 'giorno' });
        const item = el('li');
        const link = el('a', null, episodeName(other, otherDate));
        link.href = `/puntata/${other.slug}`;
        link.addEventListener('click', (event) => {
          event.preventDefault();
          openEpisode(other.slug, { push: false });
        });
        item.append(link);
        if (otherDate) item.append(document.createTextNode(' '), el('small', null, `· ${otherDate}`));
        links.append(item);
      }
      list.append(links);
    }
    const all = el('p', 'links__all');
    const allLink = el('a', null, `Tutte le puntate di ${programName}`);
    allLink.href = episodesPath(episode.programma);
    allLink.addEventListener('click', (event) => {
      event.preventDefault();
      openEpisodes(episode.programma);
    });
    all.append(allLink);
    list.append(all);

    if (document.body.classList.contains('is-wide')) mapView.fitTo(owned);

    const detailWasOpen = openVenueId !== null;
    const screenWasOpen = $('s-programma').classList.contains('is-active');
    closeDetailDom();
    showTab('s-programma', false);
    list.scrollTop = 0;
    if (!detailWasOpen && !screenWasOpen && !searchWasOpen && push) pushOverlay();
    else syncPath();
  }

  /**
   * A town's starred restaurants, on the programme's screen: `/stellati/…`.
   * The Worker writes the same (`guideCityBody` in `worker/pagine.js`).
   */
  function openGuideCity(slug, { push = true } = {}) {
    const city = guideCities.get(slug);
    if (!city) return;
    openGuideCitySlug = slug;
    openStatus = false;
    openProgramId = null;
    openZoneSlug = null;
    openEpisodeSlug = null;

    screenVenues = city.venues;
    $('prog-name').textContent = programNames.get(GUIDE_ID) ?? GUIDE_ID;
    $('prog-story').hidden = true;
    $('prog-title').textContent = guideCityTitle(city.citta);
    $('prog-sub').textContent = guideSummary(city.venues);
    const list = $('list-programma');
    renderList(list, city.venues, ctx, { address: true });

    if (city.venues.some((venue) => venue.lat !== null && venue.lng !== null)) {
      const show = el('button', 'hero__map');
      show.type = 'button';
      show.append(icon('pin'), document.createTextNode('Vedi sulla mappa'));
      show.addEventListener('click', () => focusOnMap(city.venues, `Stellati · ${city.citta}`));
      list.prepend(show);
    }

    const zone = zonesBySlug.get(zoneSlug(city.citta));
    if (zone) {
      const more = el('p', 'links__all');
      const link = el('a', null, `${city.citta}: ${count(zone.n, 'ristorante visto', 'ristoranti visti')} in TV →`);
      link.href = `/zona/${zoneSlug(city.citta)}`;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        openZone(zoneSlug(city.citta), { push: false });
      });
      more.append(link);
      list.append(more);
    }
    list.append(el('p', 'block__label', 'Altre città'));
    list.append(guideCityChips(slug, 30));
    const all = el('p', 'links__all');
    const allLink = el('a', null, 'Tutti gli stellati 2026');
    allLink.href = `/programma/${GUIDE_ID}`;
    allLink.addEventListener('click', (event) => {
      event.preventDefault();
      openProgram(GUIDE_ID, { push: false });
    });
    all.append(allLink);
    list.append(all, guideCredit());

    const detailWasOpen = openVenueId !== null;
    const screenWasOpen = $('s-programma').classList.contains('is-active');
    closeDetailDom();
    showTab('s-programma', false);
    list.scrollTop = 0;
    if (!detailWasOpen && !screenWasOpen && push) pushOverlay();
    else syncPath();
  }

  /**
   * The "Puntate" tab: every episode with a page, newest first, by month, and
   * the programmes as chips to narrow it to one. The Worker writes the same
   * at `/puntate` (`episodesBody` in `worker/pagine.js`).
   */
  let episodesProgram = null;

  /** Who won each episode, by its key: the tab names them. */
  const episodeWinners = new Map(
    venues.filter((venue) => venue.vincitore).map((venue) => [venueEpisodeKey(venue), venue.nome])
  );

  /** The newest episode of the programme the tab is narrowed to, if any. */
  function episodesLatest() {
    return episodesProgram ? episodesByMonth(episodes, episodesProgram)[0]?.episodes[0] ?? null : null;
  }

  function episodesShown() {
    return episodesProgram ? episodeCounts(episodes).get(episodesProgram) ?? 0 : episodes.bySlug.size;
  }

  /** Lands on the tab, for every programme or for one. */
  function openEpisodes(programma) {
    episodesProgram = programma;
    goToTab('s-puntate');
    $('s-puntate').querySelector('.scroll').scrollTop = 0;
  }

  function renderEpisodesTab() {
    $('puntate-sub').textContent = episodesSub(episodesShown());
    const counts = episodeCounts(episodes);

    const chips = el('div', 'chips chips--wrap episodes__chips');
    const chip = (id, name, n) => {
      const button = el('button', 'chip');
      button.type = 'button';
      button.setAttribute('aria-pressed', String(episodesProgram === id));
      button.append(el('span', 'chip__label', name), el('span', 'chip__n', String(n)));
      button.addEventListener('click', () => {
        episodesProgram = id;
        renderEpisodesTab();
        $('s-puntate').querySelector('.scroll').scrollTop = 0;
        syncPath();
      });
      return button;
    };
    chips.append(chip(null, 'Tutte', episodes.bySlug.size));
    for (const program of programs) {
      const n = counts.get(program.id);
      if (n) chips.append(chip(program.id, program.nome, n));
    }

    const fragment = document.createDocumentFragment();
    fragment.append(chips);
    for (const { mese, episodes: list } of episodesByMonth(episodes, episodesProgram)) {
      fragment.append(el('h3', 'group-label', mese ?? 'Senza data'));
      for (const episode of list) {
        const row = el('a', 'row-link');
        row.href = `/puntata/${episode.slug}`;
        const text = el('span');
        const notes = [
          episodesProgram ? null : programNames.get(episode.programma) ?? episode.programma,
          formatEpisodeDate({ data: episode.data, precisione: 'giorno' }),
          count(episode.n, 'locale', 'locali'),
          episodeWinners.has(episode.key) ? `ha vinto ${episodeWinners.get(episode.key)}` : null,
        ].filter(Boolean);
        text.append(el('b', null, episodeTitleAndTown(episode)), el('small', null, notes.join(' · ')));
        row.append(programBar(episode.programma), text, icon('chevron'));
        row.addEventListener('click', (event) => {
          event.preventDefault();
          openEpisode(episode.slug);
        });
        fragment.append(row);
      }
    }
    $('list-puntate').replaceChildren(fragment);
  }

  /**
   * The latest episodes above the list, each to its page, and the way to all
   * of them: the "Puntate" tab. The Worker writes the same
   * links into the home page (`recentEpisodesHtml` in `worker/pagine.js`).
   */
  function renderRecent() {
    const latest = onAirEpisodes(episodes);
    const box = $('ultime-puntate');
    if (!latest.length) {
      box.replaceChildren();
      return;
    }
    const list = el('ul', 'recent__list');
    for (const episode of latest) {
      const link = el('a', 'recent__link');
      link.href = `/puntata/${episode.slug}`;
      link.append(
        el('span', 'recent__title', episodeTitleAndTown(episode)),
        el('span', 'recent__prog', programNames.get(episode.programma) ?? episode.programma)
      );
      link.addEventListener('click', (event) => {
        event.preventDefault();
        openEpisode(episode.slug);
      });
      const item = el('li');
      item.append(link);
      list.append(item);
    }
    const head = el('div', 'recent__head');
    const all = el('a', 'recent__all');
    all.href = episodesPath();
    all.append(icon('tv'), document.createTextNode('Tutte le puntate'));
    all.addEventListener('click', (event) => {
      event.preventDefault();
      openEpisodes(null);
    });
    head.append(el('p', 'block__label', latest.length === 1 ? 'Ultima puntata' : 'Ultime puntate'), all);
    box.replaceChildren(head, list);
  }

  /**
   * An address the app's catalogue does not know, on a page the Worker wrote.
   *
   * The Worker answers from the database and the app from a copy of the
   * catalogue, and the copy can be behind: the night a new episode goes in, a
   * copy kept anywhere along the way does not have it yet. The app used to put
   * the bar back to `/`, and Google filed the episode's page as a redirect to
   * the home page — the sciatt on 20 October, the tapas again on the 21st,
   * after the catalogue had been tagged with the data's version. The Worker
   * would have said "not found" if the address named nothing, so here the
   * address stays and so does what the Worker wrote: the episode's venues, as
   * links. An address that is really wrong still goes back to `/`.
   */
  function keepServed() {
    if (!served) {
      syncPath();
      return;
    }
    if (served !== 'detail') showTab(served, false);
  }

  /**
   * Opens whatever the address named, once, at startup.
   *
   * An address that names nothing that exists is not worth an error page: the
   * app opens as usual and the bar is quietly put back to `/`, because the
   * likeliest cause is a venue that was renamed. Unless the Worker wrote the
   * page: then it exists, and the app is the one behind (`keepServed`).
   */
  function openFromPath() {
    const path = location.pathname;

    const venue = /^\/locale\/([a-z0-9-]+)\/?$/.exec(path);
    if (venue) {
      if (venuesById.has(venue[1])) openDetail(venue[1], { push: false });
      else keepServed();
      return;
    }

    const program = /^\/programma\/([a-z0-9-]+)\/?$/.exec(path);
    if (program) {
      if (programNames.has(program[1])) openProgram(program[1], { push: false });
      else keepServed();
      return;
    }

    const zone = /^\/zona\/([a-z0-9-]+)\/?$/.exec(path);
    if (zone) {
      if (zonesBySlug.has(zone[1])) openZone(zone[1], { push: false });
      else keepServed();
      return;
    }

    const episode = /^\/puntata\/([a-z0-9-]+)\/?$/.exec(path);
    if (episode) {
      if (episodes.bySlug.has(episode[1])) openEpisode(episode[1], { push: false });
      else keepServed();
      return;
    }

    const all = /^\/puntate(?:\/([a-z0-9-]+))?\/?$/.exec(path);
    if (all) {
      const known = !all[1] || episodeCounts(episodes).has(all[1]);
      openEpisodes(known ? all[1] ?? null : null);
      return;
    }

    if (path === STATUS_PATH || path === `${STATUS_PATH}/`) {
      openStatusPage({ push: false });
      return;
    }

    const town = /^\/stellati\/([a-z0-9-]+)\/?$/.exec(path);
    if (town) {
      if (guideCities.has(town[1])) openGuideCity(town[1], { push: false });
      else keepServed();
      return;
    }
  }

  /**
   * A pin opens its own popup on the map and nothing else moves: the popup
   * carries the name and the way into the venue's own screen. The redraw is
   * what marks the pin as the selected one.
   */
  function selectPin(venueId) {
    state.attivo = venueId;
    render();

    // Beside the map the list is already open, so the pin's card is marked and
    // brought into view there. On a phone the popup is the whole answer and
    // nothing else moves.
    if (!document.body.classList.contains('is-wide')) return;
    const card = $('list-lista').querySelector(
      `[data-venue-id="${CSS.escape(venueId)}"]`
    );
    card?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  function toggleFav(venueId) {
    toggleFavourite(state, venueId);
    if (openVenueId) syncDetailFav(openVenueId);
    render();
    syncCardFavs(venueId);
  }

  /**
   * Every heart for this venue, wherever its card is drawn. `render` redraws
   * the list and the search; the cards on a programme, an episode, a zone or
   * under a venue are drawn once when their screen opens, and their heart
   * stayed empty after a tap that had saved the place.
   */
  function syncCardFavs(venueId) {
    const saved = state.fav.has(venueId);
    const name = venuesById.get(venueId)?.nome ?? venueId;
    for (const button of document.querySelectorAll(
      `[data-venue-id="${CSS.escape(venueId)}"] .fav`
    )) {
      button.classList.toggle('is-on', saved);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute(
        'aria-label',
        saved ? `Togli ${name} dai preferiti` : `Salva ${name} nei preferiti`
      );
    }
  }

  /**
   * Re-reads the catalogue when the app comes back into view.
   *
   * `loadCatalog` runs once, at startup, so an approval made in the panel
   * stayed invisible in a tab that was already open — and the panel is a
   * separate page, so the tab always is. Coming back with the back button is
   * worse still: the page is restored whole from the back/forward cache and no
   * script of ours runs at all.
   *
   * Every field a moderation decision can move is copied over.
   *
   * It used to be three — status, date, verifier — on the reasoning that
   * "names, zones, coordinates and episodes come from the file in git and
   * change on a deploy, which loads the page afresh anyway". That was true of
   * a catalogue that lived in the repository. It has not been true since the
   * panel started writing to the database: address, pin, name, zone and phone
   * all change without a deploy now, and this refresh was quietly dropping
   * them. Verify a venue's address in the panel, come back to the tab, and the
   * card still read "indirizzo non ancora noto" — because its status had not
   * changed, so the loop below decided nothing had.
   *
   * Episode, programme and outcome stay out: nothing in the panel can move
   * them, and a venue that appeared since startup is still not adopted here —
   * it has no card, no pin and no place in the lists, and building those is
   * what a reload is for.
   */
  const REFRESHED = [
    'stato',
    'verificato_il',
    'verificato_da',
    'nome',
    'nome_in_onda',
    'zona',
    'indirizzo',
    'indirizzo_confermato',
    'telefono',
    'lat',
    'lng',
  ];
  let refreshing = false;
  let refreshedAt = 0;

  async function refreshStatuses() {
    // Switching apps on a phone fires this often, and the catalogue is only
    // good to thirty seconds anyway: asking more often than that could not
    // return anything newer, it would only re-download what the cache already
    // holds. One in flight at a time, for the same reason.
    if (refreshing || Date.now() - refreshedAt < 30000) return;
    refreshing = true;

    let fresh;
    try {
      fresh = await loadCatalog();
    } catch (error) {
      // Offline, or the catalogue is briefly away: what is on screen was true a
      // moment ago and stays. Silence here, because this runs unasked.
      console.warn('Catalogo non riletto:', error);
      return;
    } finally {
      refreshing = false;
      refreshedAt = Date.now();
    }

    let changed = false;
    for (const next of fresh.venues) {
      // A venue added since startup is not adopted here: it has no card, no pin
      // and no place in the lists, and building those is what a reload is for.
      const current = venuesById.get(next.id);
      if (!current) continue;

      for (const field of REFRESHED) {
        if (current[field] === next[field]) continue;
        current[field] = next[field];
        changed = true;
      }
    }

    if (!changed) return;

    render();
    if (openVenueId) {
      const body = $('d-body');
      const scrolled = body.scrollTop;
      renderDetail(body, venuesById.get(openVenueId), ctx);
      body.scrollTop = scrolled;
    }
  }

  /**
   * Asked for once, and only after something has been asked of it.
   *
   * A second refusal costs another browser prompt and returns the same answer,
   * so a refusal is remembered for the visit. `locate` never rejects: without
   * a position it answers with none, and says so.
   */
  let located = false;
  let locating = false;

  /**
   * The button says which of the three things is true.
   *
   * It used to look identical before being pressed, while waiting, and after
   * being refused — and a refusal is the case that most needs saying, because
   * nothing else on screen changes: the list keeps its order and the visitor
   * is left to work out why.
   */
  function setLocateState(phase) {
    for (const id of ['btn-locate', 'btn-locate-2']) {
      const button = $(id);
      button.classList.toggle('is-busy', phase === 'cerco');
      button.classList.toggle('is-on', phase === 'trovato');
      button.classList.toggle('is-off', phase === 'negato');
      button.setAttribute('aria-busy', String(phase === 'cerco'));
    }
  }

  async function onLocate() {
    // A second press while the browser is still asking changes nothing and
    // would only start a second wait.
    if (locating) return;
    located = true;
    locating = true;
    setLocateState('cerco');

    const position = await locate();
    locating = false;
    state.me = position.coords;
    state.meExact = position.exact;
    state.meLabel = position.label;
    setLocateState(position.exact ? 'trovato' : 'negato');
    if (position.coords) mapView.setMe(position.coords);
    render();
  }

  /* ---------- wiring ---------- */

  $('tabbar').addEventListener('click', (event) => {
    const tab = event.target.closest('.tab');
    if (!tab) return;
    goToTab(tab.dataset.tab);
  });

  /**
   * Lands on a tab with nothing over it, whatever was open.
   */
  function goToTab(id) {
    mapFocus = null;
    // Give back every overlay's history entry in one step, then close them
    // here and land on the tab. Going back one entry only was fine with one
    // overlay; with a report open over a card it left an entry behind, and
    // the next back press did nothing anybody could see.
    const depth = overlayDepth;
    if (depth > 0) {
      overlayDepth = 0;
      unwinding = true;
      history.go(-depth);
    }
    if ($('report').classList.contains('is-open')) closeReportDom();
    if ($('detail').classList.contains('is-open')) closeDetailDom();
    else openVenueId = null;
    if ($('search').classList.contains('is-open')) closeSearchDom();
    openProgramId = null;
    openZoneSlug = null;
    openEpisodeSlug = null;
    showTab(id);
    if (!depth) syncPath();
  }

  /**
   * From a card to the same venue on the map: the card and whatever was under
   * it close, exactly as tapping the map tab would close them, and the map
   * flies to the pin it now draws as the selected one. Back from the map is
   * then the same as from any tab. The popup brings the card back.
   */
  function showOnMap(venueId) {
    const venue = venuesById.get(venueId);
    if (!venue || venue.lat === null || venue.lng === null) return;
    // The guide is off the map until its chip is on: turned on here, or the
    // map would fly to a pin it is not drawing.
    if (isGuide(venue)) state.filtri.programmi.add(GUIDE_ID);
    goToTab('s-mappa');
    state.attivo = venueId;
    render();
    mapView.flyTo(venue, { popup: true });
  }

  for (const button of document.querySelectorAll('[data-back-tab]')) {
    button.addEventListener('click', () => leaveOverlay(() => showTab(state.tab)));
  }

  $('close-detail').addEventListener('click', () => leaveOverlay(closeDetailDom));
  $('close-report').addEventListener('click', () => leaveOverlay(closeReportDom));
  $('d-fav').addEventListener('click', () => {
    if (openVenueId) toggleFav(openVenueId);
  });

  /**
   * Opens the search overlay, and decides what inside it takes the focus.
   *
   * That is not the same question for the two doors into it. Somebody who
   * taps a search control has come to type, so the field takes the focus and
   * the on-screen keyboard is what they asked for. Somebody who taps the
   * filter button has not: focusing the field there slides the keyboard up
   * over the very chips they came to press, and they have to dismiss it
   * before they can do anything at all.
   *
   * The first chip takes the focus instead, rather than nothing at all. The
   * focus has to end up inside the overlay — left on the filter button it
   * would be sitting on a control the overlay now covers, which is where a
   * keyboard would carry on tabbing — and the chips are what the visitor came
   * for. A button raises no keyboard.
   */
  const openSearch = ({ focus = 'query' } = {}) => {
    if ($('search').classList.contains('is-open')) return;
    searchFromMap = state.tab === 's-mappa';
    $('search').classList.add('is-open');
    // Before the focus, because until this runs there are no chips to focus.
    render();
    takeFocus(
      focus === 'map' || (focus === 'query' && document.body.classList.contains('is-wide'))
        ? $('q-map')
        : focus === 'query'
          ? $('q')
          : $('chips-search').querySelector('.chip:not(:disabled)')
    );
    pushOverlay();
  };
  // Wrapped rather than passed straight in: a listener is handed the event as
  // its first argument, and the options object has to be ours.
  $('open-search').addEventListener('click', () => openSearch());
  $('open-search-2').addEventListener('click', () => openSearch());
  // The map's field beside the list: typing there is typing in the search,
  // which opens in the column on the left with the caret left where it is.
  // On a click or a keystroke, not on focus: closing the search hands the
  // focus back to this field, and that must not open it again.
  $('q-map').addEventListener('click', () => openSearch({ focus: 'map' }));
  $('q-map').addEventListener('input', (event) => {
    openSearch({ focus: 'map' });
    $('q').value = event.target.value;
    $('q').dispatchEvent(new Event('input'));
  });
  $('close-search').addEventListener('click', () => leaveOverlay(closeSearchDom));
  $('close-search-map').addEventListener('click', () => $('close-search').click());
  // Whether the search is up, for the "Chiudi" beside the map's field. Read
  // off the panel itself, because half a dozen paths open and close it.
  new MutationObserver(() => {
    document.body.classList.toggle('is-searching', $('search').classList.contains('is-open'));
  }).observe($('search'), { attributes: true, attributeFilter: ['class'] });

  /*
   * Esc closes whatever is on top — the report, then the card, then the
   * search — by pressing its own close button, so the history entry, the
   * address, the title and the focus all go exactly as they do for a tap.
   * Left alone while a field is composing text (an accent being typed, an IME),
   * where Esc belongs to the keyboard, not to the page.
   */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) return;
    const top = [
      ['report', 'close-report'],
      ['detail', 'close-detail'],
      ['search', 'close-search'],
    ].find(([panel]) => $(panel).classList.contains('is-open'));
    if (!top) return;
    event.preventDefault();
    $(top[1]).click();
  });
  $('search-show').addEventListener('click', () => {
    // The map framed what the list showed, query included; once the query is
    // dropped it has to frame what is left.
    refitWanted = true;
    leaveOverlay(closeSearchDom);
  });
  $('search-reset').addEventListener('click', () => {
    clearFilters(state.filtri);
    onFiltersChanged();
  });
  /**
   * The query is read at once and acted on a moment later.
   *
   * `state.q` moves on the keystroke, because the field and the state must
   * never disagree. The redraw waits, because it rebuilds the chips, a list
   * and the map's source: on ninety locali that is invisible, and it was
   * measured at twenty thousand, where doing it per keystroke is the
   * difference between a field that types and one that stutters.
   */
  let queryTimer = 0;
  $('q').addEventListener('input', (event) => {
    state.q = event.target.value;
    searchEpisodesAll = false;
    clearTimeout(queryTimer);
    queryTimer = setTimeout(() => {
      resetLists();
      render();
    }, 120);
  });

  // Filters live on the search screen, which is the one place with room to
  // lay them out; the map's button is a second door into it — and it opens on
  // the filters, not on the keyboard.
  $('btn-filters').addEventListener('click', () => openSearch({ focus: 'filters' }));
  $('btn-locate').addEventListener('click', onLocate);
  $('btn-locate-2').addEventListener('click', onLocate);

  /*
   * "Contribuisci" leads somewhere now.
   *
   * Its only behaviour used to be disabling itself with a note saying reports
   * were not open yet. The note was true of the thing the button named —
   * adding a venue the catalogue does not have, which really is not built, and
   * the API refuses an unknown id on purpose — but reports themselves have
   * worked for days, from inside each venue's card. So the only contribution
   * route reachable from the tab bar was the dead one, and the live one was
   * three taps down.
   *
   * The button now names what exists and opens the search, because correcting
   * a venue starts with choosing it.
   */
  $('btn-segnala').addEventListener('click', () => openSearch());

  $('seg-ordine').addEventListener('click', (event) => {
    const button = event.target.closest('.seg__btn');
    if (!button) return;
    state.ordine = button.dataset.ordine;
    // Sorting by distance with no position silently falls back to the episode
    // order, which looks like a broken button. Ask for the position instead;
    // `onLocate` redraws when it has an answer.
    if (state.ordine === 'vicini' && !located) onLocate();
    else render();
  });

  $('map-count').addEventListener('click', () => {
    if (!mapFocus) {
      showTab('s-lista');
      return;
    }
    mapFocus = null;
    refitWanted = true;
    render();
  });

  /**
   * The wide layout, in the part of it that a stylesheet cannot do.
   *
   * `.is-wide` mirrors the 1000px query for the rules that need a class, but
   * two things have to happen in script. The map is laid out in a column that
   * has just changed width and does not measure itself. And the "Mappa" tab
   * has nothing left to show once the map is permanently on screen, so the
   * column moves to the list, and moves back if the window narrows again and
   * the visitor has not chosen something else in the meantime.
   */
  const wideQuery = window.matchMedia('(min-width: 1000px)');
  let tabBeforeWide = null;

  function applyLayout() {
    const wide = wideQuery.matches;
    document.body.classList.toggle('is-wide', wide);

    if (wide && state.tab === 's-mappa') {
      tabBeforeWide = state.tab;
      showTab('s-lista');
    } else if (!wide && tabBeforeWide && state.tab === 's-lista') {
      showTab(tabBeforeWide);
      tabBeforeWide = null;
    }

    mapView.setZoomControls(wide);
    setTimeout(() => mapView.resize(), 60);
  }

  wideQuery.addEventListener('change', () => {
    applyLayout();
    // A phone turned or a window widened: the map is now on screen beside the
    // list, so it is time to fetch it if a narrow start held it back.
    if (wideQuery.matches) mapView.load();
  });

  // Two ways back into an app left open: returning to the tab, and the back
  // button. The second restores the page from the back/forward cache without
  // re-running anything, so it needs its own signal. The catalogue's own
  // thirty-second cache keeps a burst of tab-switching from becoming a burst of
  // requests.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshStatuses();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshStatuses();
  });

  catalogReady = true;
  renderLegend();
  renderRecent();
  render();
  applyLayout();
  openFromPath();

  /*
   * The map is fetched once the page it sits behind is on screen, and only
   * where it will be seen.
   *
   * On a wide window the map is always beside the list, so it is fetched as
   * soon as the browser is idle. On a phone it is fetched here only if the
   * map is the screen showing; otherwise it waits for `showTab('s-mappa')`
   * — the map tab, "Vedi sulla mappa" — or for the window to widen. A phone
   * opening a `/locale/…`, `/puntata/…` or `/zona/…` link from a search used to
   * download two megabytes of tiles and spend seconds starting MapLibre behind
   * a screen that covers the map, while the page's own heading waited: on a
   * mid-range phone the episode page took seven seconds to show its title.
   *
   * Idle rather than immediate, so it queues behind the first paint rather
   * than competing with it. `requestIdleCallback` is not in every browser; the
   * timeout is the same promise, made less precisely.
   */
  const fetchMap = () => {
    if (wideQuery.matches) mapView.load();
    else loadMapIfShown();
  };
  if (window.requestIdleCallback) requestIdleCallback(fetchMap, { timeout: 2000 });
  else setTimeout(fetchMap, 400);

  if (!isMapConfigured()) {
    console.warn(
      'VITE_MAPTILER_KEY non impostata: la mappa resta disattivata, il catalogo funziona.'
    );
  }
}

start();
